import { Autocomplete, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { useFormikContext } from "formik";
import React, { useMemo, useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { applyDocumentMask, determineDocumentType } from "@/helpers/document-helpers";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useLoading from "@/hooks/use-loading";
import { ActivationFormData, CompanyType } from "@/interfaces/talk2pay.iterfaces";
import { ActivationMessages } from "@/messages/platforms/talk2PayConnection/activationMessages";
import { DocumentType } from "@/types/document.types";

import { translateCompanyType } from "../formData";

export function FormFields({ messages }: { messages: ActivationMessages }) {
  const formik = useFormikContext<ActivationFormData>();
  const addressNumberRef = useRef<HTMLInputElement>(null);
  const loadingIconColor = "primary";
  const { startLoading, endLoading, isLoading, loadingIcon } = useLoading({ loadingIconColor });

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;

    const formattedValue = removeNonIntegersFromString(value);
    formik.setFieldError("zipCode", undefined);
    formik.setFieldValue("zipCode", formattedValue);
    const isCEPCompleted = formattedValue.length === 8;
    if (!isCEPCompleted) {
      formik.setFieldValue("address", "");
      formik.setFieldValue("province", "");
    } else {
      startLoading();
      try {
        const cepData = await cep(formattedValue);
        formik.setFieldValue("address", cepData.street);
        formik.setFieldValue("province", cepData.neighborhood);

        setTimeout(() => {
          addressNumberRef.current?.focus();
        }, 0);
      } catch (err) {
        formik.setFieldError("zipCode", messages.cepNotFoundError);
        formik.setFieldValue("address", "");
        formik.setFieldValue("province", "");
      }
      endLoading();
    }
  };

  const handlePhoneChange = (name: string, value: string) => {
    let formattedPhone = removeNonIntegersFromString(value);

    if (formattedPhone.startsWith("55") && formattedPhone.length > 11) {
      formattedPhone = formattedPhone.slice(2);
    }

    formik.setFieldValue(name, formattedPhone);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("document", e.target.value);
    formik.setFieldTouched("document", true, false);
  };

  const isCPF = useMemo(() => {
    const cleanedValue = removeNonIntegersFromString(formik.values.document || "");
    return cleanedValue.length === 11;
  }, [formik.values.document]);

  const onRedirect = () => {
    window.open(
      "https://central.ajuda.asaas.com/hc/pt-br/articles/32096847160859-Termos-e-Condi%C3%A7%C3%B5es-de-Uso",
      "_blank"
    );
  };

  return (
    <MDBox mt={1.625}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InputMaskWrapper
            mask={applyDocumentMask(formik.values.document)}
            value={formik.values.document}
            onChange={handleDocumentChange}
            variant="standard"
            name="document"
            label={messages.documentLabel}
            fullWidth
            error={formik.touched.document && !!formik.errors.document}
            helperText={formik.touched.document && formik.errors.document}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 18 }}
            dynamicMask={true}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="text"
            variant="standard"
            label={messages.nameLabel}
            name="name"
            value={formik.values.name}
            placeholder={messages.namePlaceholder}
            error={formik.touched.name && !!formik.errors.name}
            success={formik.values.name.length > 0 && !formik.errors.name}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="email"
            variant="standard"
            label={messages.emailLabel}
            name="email"
            value={formik.values.email}
            placeholder={messages.emailPlaceholder}
            error={formik.touched.email && !!formik.errors.email}
            success={formik.values.email.length > 0 && !formik.errors.email}
          />
        </Grid>
      </Grid>

      {isCPF && (
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormField
              type="date"
              variant="standard"
              label={messages.birthDateLabel}
              name="birthDate"
              value={formik.values.birthDate || ""}
              InputLabelProps={{ shrink: true }}
              error={formik.touched.birthDate && !!formik.errors.birthDate}
              helperText={formik.touched.birthDate && formik.errors.birthDate}
              sx={{
                ...(formik.touched.birthDate &&
                  formik.errors.birthDate && {
                    backgroundPosition: "right 1.3rem center !important",
                  }),
              }}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InputMaskWrapper
            mask="(99) 99999-9999"
            value={formik.values.mobilePhone}
            onChange={(e) => handlePhoneChange("mobilePhone", e.target.value)}
            variant="standard"
            name="mobilePhone"
            label={messages.mobilePhoneLabel}
            fullWidth
            error={formik.touched.mobilePhone && !!formik.errors.mobilePhone}
            helperText={formik.touched.mobilePhone && formik.errors.mobilePhone}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            fullWidth
            value={
              formik.values.companyType
                ? {
                    value: formik.values.companyType,
                    label: translateCompanyType(formik.values.companyType, messages).label,
                  }
                : null
            }
            options={Object.values(CompanyType).map((type) => {
              const { value, label } = translateCompanyType(type, messages);
              return { value, label };
            })}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, newValue) => {
              if (newValue) formik.setFieldValue("companyType", newValue.value);
            }}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={messages.companyTypeLabel}
                variant="standard"
                error={formik.touched.companyType && !!formik.errors.companyType}
                helperText={formik.touched.companyType && formik.errors.companyType}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <CurrencyInput
            name="incomeValue"
            label={messages.incomeValueLabel}
            value={formik.values.incomeValue}
            onChange={formik.handleChange}
            fullWidth
            error={formik.touched.incomeValue && !!formik.errors.incomeValue}
            helperText={formik.touched.incomeValue && formik.errors.incomeValue}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <MDBox my={2}>
        <MDTypography variant="h5">{messages.addressSubtitle}</MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InputMaskWrapper
            mask="99999-999"
            value={formik.values.zipCode}
            onChange={handleCEPChange}
            variant="standard"
            name="zipCode"
            label={messages.cepLabel}
            fullWidth
            error={formik.touched.zipCode && !!formik.errors.zipCode}
            helperText={formik.touched.zipCode && formik.errors.zipCode}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="text"
            variant="standard"
            label={messages.addressLabel}
            name="address"
            value={formik.values.address}
            placeholder={messages.addressPlaceholder}
            error={formik.touched.address && !!formik.errors.address}
            success={formik.values.address.length > 0 && !formik.errors.address}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="text"
            variant="standard"
            label={messages.addressNumberLabel}
            name="addressNumber"
            value={formik.values.addressNumber}
            placeholder={messages.addressNumberPlaceholder}
            error={formik.touched.addressNumber && !!formik.errors.addressNumber}
            success={formik.values.addressNumber.length > 0 && !formik.errors.addressNumber}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="text"
            variant="standard"
            label={messages.complementLabel}
            name="complement"
            value={formik.values.complement}
            placeholder={messages.complementPlaceholder}
            error={formik.touched.complement && !!formik.errors.complement}
            success={
              formik.values.complement &&
              formik.values.complement.length > 0 &&
              !formik.errors.complement
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField
            type="text"
            variant="standard"
            label={messages.provinceLabel}
            name="province"
            value={formik.values.province}
            placeholder={messages.provincePlaceholder}
            error={formik.touched.province && !!formik.errors.province}
            success={formik.values.province.length > 0 && !formik.errors.province}
          />
        </Grid>
      </Grid>

      <MDBox mt={1}>
        <MDTypography variant="caption" color="text">
          {messages.termsOfUseMessagePart1}
          <MDTypography
            onClick={onRedirect}
            variant="caption"
            fontWeight="medium"
            textGradient
            sx={{ cursor: "pointer" }}
            color="dark"
          >
            {messages.termsOfUseLinkText}
          </MDTypography>
          {messages.termsOfUseMessagePart2}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}
